/**
 * BeezFy Product Card v3.0 - Ultimate Fashion Edition
 * Premium product card interaction system
 */

// Wrap entire module to avoid leaking globals
(() => {
  if (window.beezfyProductCardV3) return;
  window.beezfyProductCardV3 = true;

  window.BEEZFY_CARD_DEBUG = window.BEEZFY_CARD_DEBUG || false;
  const cardDebug = (...args) => { if (window.BEEZFY_CARD_DEBUG) console.log(...args); };

class BeezfyProductCardV3 {
  constructor() {
    this.cards = [];
    this.wishlist = this.loadWishlist();
    this.compare = this.loadCompare();
    this.swatchDataStore = window.BeezfySwatchDataStore || null;
    this.init();
  }
  
  init() {
    // Initialize all product cards
    const cards = document.querySelectorAll('[data-product-card]');
    cardDebug('[BeezFy Card v3] Found', cards.length, 'product cards');

    cards.forEach((card, index) => {
      cardDebug('[BeezFy Card v3] Initializing card', index + 1);
      this.initCard(card);
    });
    
    // Global event listeners
    this.initGlobalListeners();

    // Initialize analytics if enabled
    this.initAnalytics();

    // Re-inizializza le card aggiunte dopo (prodotti consigliati/correlati caricati via AJAX,
    // <product-recommendations>, quick view, ecc.) così quick view/swatch/wizard funzionano ovunque.
    this.observeNewCards();
  }

  observeNewCards() {
    try {
      const self = this;
      const obs = new MutationObserver((muts) => {
        for (const m of muts) {
          for (const node of m.addedNodes) {
            if (!node || node.nodeType !== 1) continue;
            if (node.matches && node.matches('[data-product-card]')) self.initCard(node);
            if (node.querySelectorAll) node.querySelectorAll('[data-product-card]').forEach((c) => self.initCard(c));
          }
        }
      });
      obs.observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
  }
  
  initCard(card) {
    if (card.dataset.bpcInit) return; // già inizializzata (re-scan card dinamiche: consigliati/correlati)
    card.dataset.bpcInit = '1';
    const cardData = {
      element: card,
      productId: card.dataset.productId,
      productHandle: card.dataset.productHandle,
      index: parseInt(card.dataset.cardIndex),
      layout: card.dataset.cardLayout,
      context: card.dataset.cardContext,
      atcMode: card.dataset.atcMode,
      mediaCache: new Map()
    };
    
    this.cards.push(cardData);

    this.cacheInitialMedia(card);

    // Carica l'immagine hover solo sui device che hanno davvero l'hover.
    // Su touch resta con data-src (mai scaricata) → dimezza la memoria immagini
    // e permette al load-more di reggere molte pagine senza crash del webview.
    this.promoteHoverImage(card);

    // Initialize card components
    this.initSwatches(card);
    this.initSizes(card);
    this.initActions(card);
    this.initAddToCart(card);
    this.initHoverEffects(card);
    
    // Track impression
    if (card.dataset.analyticsEnabled) {
      this.trackImpression(cardData);
    }
  }
  
  initSwatches(card) {
    const swatches = card.querySelectorAll('[data-swatch]');
    const swatchContainers = card.querySelectorAll('[data-swatches-container]');

    if (this.swatchDataStore) {
      swatchContainers.forEach((container) => this.swatchDataStore.enrichContainer(container));
    }

    swatches.forEach(swatch => {
      swatch.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const container = swatch.closest('[data-swatches-container]');
        const variantId = swatch.dataset.variantId;
        const optionValue = swatch.dataset.optionValue;
        const optionName = swatch.dataset.optionName || container?.dataset.optionName;

        // Update active state
        container.querySelectorAll('[data-swatch]').forEach(s => {
          s.classList.remove('bz-swatch--active');
        });
        swatch.classList.add('bz-swatch--active');

        // Update product image if available
        await this.updateProductImage(card, variantId, {
          optionName,
          optionValue,
          swatchImage: swatch.dataset.swatchImage,
          swatchColor: swatch.dataset.swatchColor
        });

        if (variantId) {
          this.updateSelectedVariant(card, variantId);
        }

        // Track event
        this.trackEvent('swatch_select', {
          product_id: card.dataset.productId,
          variant_id: variantId,
          option_value: optionValue
        });
      });

      // Hover preview on desktop
      if (window.matchMedia('(hover: hover)').matches) {
        swatch.addEventListener('mouseenter', () => {
          const variantId = swatch.dataset.variantId;
          const optionName = swatch.dataset.optionName || swatch.closest('[data-swatches-container]')?.dataset.optionName;
          const optionValue = swatch.dataset.optionValue;
          this.previewVariant(card, variantId, {
            optionName,
            optionValue,
            swatchImage: swatch.dataset.swatchImage
          });
        });

        swatch.addEventListener('mouseleave', () => {
          this.restoreOriginalImage(card);
        });
      }
    });
  }
  
  initSizes(card) {
    const sizeChips = card.querySelectorAll('[data-size-chip]');
    
    sizeChips.forEach(chip => {
      chip.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const container = chip.closest('[data-sizes-container]');
        const variantId = chip.dataset.variantId;
        const optionValue = chip.dataset.optionValue;
        
        // Update selected state
        container.querySelectorAll('[data-size-chip]').forEach(c => {
          c.classList.remove('bz-size-chip--selected');
        });
        chip.classList.add('bz-size-chip--selected');
        
        // Update variant selection
        this.updateSelectedVariant(card, variantId);
        
        // Track event
        this.trackEvent('size_select', {
          product_id: card.dataset.productId,
          variant_id: variantId,
          option_value: optionValue
        });
      });
    });
  }
  
  initActions(card) {
    // Wishlist button
    const wishlistBtn = card.querySelector('[data-wishlist-btn]');
    if (wishlistBtn) {
      const productId = wishlistBtn.dataset.productId;
      
      // Set initial state from localStorage
      const wishlistData = JSON.parse(localStorage.getItem('beezfy-wishlist')) || [];
      if (wishlistData.includes(productId)) {
        wishlistBtn.classList.add('bz-card__action-btn--active');
        this.updateWishlistIcon(wishlistBtn, true);
      }
      
      wishlistBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleWishlist(productId, wishlistBtn);
      });
    }
    
    // Quick view button
    const quickViewBtn = card.querySelector('[data-quickview-btn]');
    if (quickViewBtn) {
      quickViewBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openQuickView(quickViewBtn.dataset.productHandle, card);
      });
    }
    
    // Compare button
    const compareBtn = card.querySelector('[data-compare-btn]');
    if (compareBtn) {
      const productId = compareBtn.dataset.productId;
      
      // Set initial state from sessionStorage
      const compareData = JSON.parse(sessionStorage.getItem('beezfy-compare')) || [];
      if (compareData.includes(productId)) {
        compareBtn.classList.add('bz-card__action-btn--active');
      }
      
      compareBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleCompare(productId, compareBtn);
      });
    }
    
    // Share button
    const shareBtn = card.querySelector('[data-share-btn]');
    if (shareBtn) {
      shareBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.shareProduct(shareBtn.dataset.productUrl, shareBtn.dataset.productTitle);
      });
    }
  }
  
  initAddToCart(card) {
    const atcMode = card.dataset.atcMode || 'direct';
    const productHandle = card.dataset.productHandle;
    const productId = card.dataset.productId;

    cardDebug('[BeezFy Card v3] Init ATC - Handle:', productHandle, 'ID:', productId, 'Mode:', atcMode);

    // Validate required data
    if (!productHandle || productHandle === 'undefined') {
      console.error('[BeezFy Card v3] Missing product handle for card:', card);
      return;
    }

    // Read global wizard settings
    const wizardEnabled = window.themeSettings?.enable_variant_wizard !== false;
    const triggerMode = window.themeSettings?.wizard_trigger_mode || 'always';

    // Helper to check if wizard should open
    const maybeOpenWizard = async () => {
      if (!wizardEnabled) {
        cardDebug('[BeezFy Card v3] Wizard disabled globally');
        return false;
      }

      // Validate productHandle
      if (!productHandle || productHandle === 'undefined') {
        console.error('[BeezFy Card v3] Invalid product handle in maybeOpenWizard');
        return false;
      }

      // Always open wizard for wizard modes
      if (['wizard', 'wizard_required', 'wizard-required'].includes(atcMode)) {
        cardDebug('[BeezFy Card v3] Wizard required by atcMode:', atcMode);
        return await this.openVariantWizard(productHandle, productId);
      }

      // Check if product has multiple variants or options
      try {
        cardDebug('[BeezFy Card v3] Checking product variants for:', productHandle);
        const resp = await fetch(`/products/${productHandle}.js`);

        if (!resp.ok) {
          console.error('[BeezFy Card v3] Failed to fetch product:', productHandle);
          // In caso di errore, apri wizard per sicurezza
          return await this.openVariantWizard(productHandle, productId);
        }

        const product = await resp.json();
        const hasMultipleVariants = product?.variants?.length > 1;
        const hasOptions = product?.options?.some(opt => opt.values?.length > 1);
        const needsWizard = hasMultipleVariants || hasOptions;

        cardDebug('[BeezFy Card v3] Product analysis:', {
          handle: productHandle,
          variants: product?.variants?.length,
          hasMultipleVariants,
          hasOptions,
          needsWizard,
          triggerMode
        });

        // Apri wizard se necessario
        if (needsWizard || triggerMode === 'always') {
          return await this.openVariantWizard(productHandle, productId);
        }
      } catch (err) {
        console.error('[BeezFy Card v3] Error checking variants:', err);
        // In caso di errore, apri wizard per sicurezza
        return await this.openVariantWizard(productHandle, productId);
      }
      return false;
    };
    
    // Direct add to cart form
    const productForm = card.querySelector('[data-product-form]');
    if (productForm) {
      cardDebug('[BeezFy Card v3] Found product form, adding submit handler');
      
      // Prevent form from opening product page
      productForm.addEventListener('click', (e) => {
        e.stopPropagation();
      });
      
      productForm.addEventListener('submit', async (e) => {
        // NON bloccare il submit se c'è il nuovo sistema ATC attivo
        if (window.BeezfyATCSystem && window.BeezfyTheme?.settings?.atc_ajax_add !== false) {
          cardDebug('[BeezFy Card v3] Delegating to BeezFy ATC System');
          // Il sistema ATC gestirà tutto, non fare altro
          // Non chiamare preventDefault() per permettere al sistema ATC di intercettare
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        cardDebug('[BeezFy Card v3] Form submitted (legacy mode)');

        const variantId = productForm.querySelector('[name="id"]')?.value;
        const button = productForm.querySelector('[data-atc-button]');

        // PRIMA controlla SEMPRE se il prodotto ha varianti
        // Ignora completamente il variant preselezionato
        const mustOpenWizard = async () => {
          try {
            cardDebug('[BeezFy Card v3] Checking product variants for wizard:', productHandle);
            const resp = await fetch(`/products/${productHandle}.js`);
            if (resp.ok) {
              const product = await resp.json();
              const hasMultipleVariants = product?.variants?.length > 1;
              const hasOptions = product?.options?.some(opt => opt.values?.length > 1);

              cardDebug('[BeezFy Card v3] Product check:', {
                handle: productHandle,
                variantsCount: product?.variants?.length,
                hasMultipleVariants,
                hasOptions,
                variantId: variantId
              });

              // Se ha più di una variante O ha opzioni, SEMPRE apri wizard
              if (hasMultipleVariants || hasOptions) {
                cardDebug('[BeezFy Card v3] FORCING WIZARD - Product has multiple variants/options');
                return true;
              }
            }
          } catch (err) {
            console.error('[BeezFy Card v3] Error checking product, opening wizard for safety:', err);
            return true; // In caso di errore, apri wizard per sicurezza
          }
          return false;
        };

        // PRIMO CHECK: Se il prodotto ha varianti, apri SEMPRE il wizard
        const forceWizard = await mustOpenWizard();
        if (forceWizard || ['wizard', 'wizard_required', 'wizard-required'].includes(atcMode)) {
          cardDebug('[BeezFy Card v3] Opening wizard (forced or required)');
          const opened = await maybeOpenWizard();
          if (opened !== false) return false;
        }

        // SOLO se il prodotto NON ha varianti, procedi con add to cart
        if (!variantId || variantId === '' || variantId === 'undefined') {
          cardDebug('[BeezFy Card v3] No variant for single-variant product');
          this.showNotification('Seleziona una variante', 'warning');
          return false;
        }
        
        const quantity = productForm.querySelector('[name="quantity"]')?.value || 1;
        cardDebug('[BeezFy Card v3] Adding to cart:', { variantId, quantity });
        
        await this.addToCart(variantId, quantity, button);
        return false;
      });
      
      // Also handle button click directly
      const atcButton = productForm.querySelector('[data-atc-button]');
      if (atcButton) {
        atcButton.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          cardDebug('[BeezFy Card v3] ATC button clicked directly');

          // PRIMA controlla SEMPRE se il prodotto ha varianti
          const checkVariants = async () => {
            try {
              const resp = await fetch(`/products/${productHandle}.js`);
              if (resp.ok) {
                const product = await resp.json();
                const hasVariants = product?.variants?.length > 1 || product?.options?.some(opt => opt.values?.length > 1);
                cardDebug('[BeezFy Card v3] Button click variant check:', {
                  handle: productHandle,
                  hasVariants,
                  variantsCount: product?.variants?.length
                });
                return hasVariants;
              }
            } catch (err) {
              console.error('[BeezFy Card v3] Error in variant check:', err);
              return true; // Apri wizard in caso di errore
            }
            return false;
          };

          // Se ha varianti, SEMPRE apri il wizard
          if (await checkVariants()) {
            cardDebug('[BeezFy Card v3] Product has variants - opening wizard');
            const opened = await maybeOpenWizard();
            return false;
          }

          // Solo per prodotti senza varianti
          const variantId = productForm.querySelector('[name="id"]')?.value;
          if (!variantId || variantId === '' || variantId === 'undefined') {
            this.showNotification('Seleziona una variante', 'warning');
            return false;
          }
          
          const quantity = productForm.querySelector('[name="quantity"]')?.value || 1;
          await this.addToCart(variantId, quantity, atcButton);
          return false;
        });
      }
    }
    
    // Variant wizard trigger
    const wizardTriggers = card.querySelectorAll('[data-variant-wizard-trigger]');
    wizardTriggers.forEach(trigger => {
      trigger.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const productHandle = trigger.dataset.productHandle;
        const productId = trigger.dataset.productId;
        
        cardDebug('[BeezFy Card v3] Wizard trigger clicked:', { productHandle, productId });
        
        // Always use the wizard for products with multiple variants
        await this.openVariantWizard(productHandle, productId);
      });
    });
    
    // Quick add inline
    const quickSelect = card.querySelector('[data-quick-variant-select]');
    const quickAddBtn = card.querySelector('[data-quick-add-button]');
    
    if (quickSelect && quickAddBtn) {
      quickSelect.addEventListener('change', () => {
        const variantId = quickSelect.value;
        quickAddBtn.disabled = !variantId;
        quickAddBtn.dataset.variantId = variantId;
      });
      
      quickAddBtn.addEventListener('click', async (e) => {
        e.preventDefault();

        // SEMPRE controlla se ha varianti multiple prima
        const checkNeedsWizard = async () => {
          try {
            const handle = card.dataset.productHandle;
            const resp = await fetch(`/products/${handle}.js`);
            if (resp.ok) {
              const product = await resp.json();
              return product?.variants?.length > 1 || product?.options?.some(opt => opt.values?.length > 1);
            }
          } catch (err) {}
          return true; // In caso di errore, apri wizard
        };

        if (await checkNeedsWizard()) {
          const handle = card.dataset.productHandle;
          const id = card.dataset.productId;
          await this.openVariantWizard(handle, id);
          return;
        }

        // Solo per prodotti senza varianti
        const variantId = quickAddBtn.dataset.variantId;
        if (variantId) {
          await this.addToCart(variantId, 1, quickAddBtn);
          quickSelect.value = ''; // Reset selection
          quickAddBtn.disabled = true;
        }
      });
    }
  }
  
  promoteHoverImage(card) {
    // Rileva una sola volta se il device può fare hover (desktop/mouse).
    if (this._canHover === undefined) {
      try {
        this._canHover = window.matchMedia('(hover: hover)').matches;
      } catch (e) {
        this._canHover = true; // fallback prudente
      }
    }
    if (!this._canHover) return; // touch: lascia data-src, l'immagine non verrà mai scaricata

    card.querySelectorAll('img[data-hover-image][data-src]').forEach((img) => {
      if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
      img.setAttribute('src', img.dataset.src);
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    });
  }

  initHoverEffects(card) {
    const mediaContainer = card.querySelector('[data-media-container]');
    if (!mediaContainer) return;
    
    // Video hover play
    const hoverVideo = mediaContainer.querySelector('[data-hover-video]');
    if (hoverVideo) {
      mediaContainer.addEventListener('mouseenter', () => {
        hoverVideo.play().catch(() => {});
      });
      
      mediaContainer.addEventListener('mouseleave', () => {
        hoverVideo.pause();
        hoverVideo.currentTime = 0;
      });
    }
  }
  
  async addToCart(variantId, quantity, button) {
    cardDebug('[BeezFy Card v3] Adding to cart:', { variantId, quantity });

    // Always use unified ATC System if available
    if (window.BeezfyATCSystem && typeof window.BeezfyATCSystem.addToCart === 'function') {
      cardDebug('[BeezFy Card v3] Using Unified ATC System');
      try {
        await window.BeezfyATCSystem.addToCart(variantId, quantity, {
          button,
          source: 'product-card'
        });

        // Track event
        this.trackEvent('add_to_cart', {
          variant_id: variantId,
          quantity: quantity
        });
        return; // Exit here, let ATC system handle everything
      } catch (error) {
        cardDebug('[BeezFy Card v3] ATC System error:', error);
        // Continue with fallback if error
      }
    }

    // Fallback only if unified system not available
    cardDebug('[BeezFy Card v3] Unified ATC not available, using fallback');
    this.setButtonLoading(button, true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: parseInt(quantity, 10)
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[BeezFy Card v3] Cart error:', errorData);
        throw new Error(errorData?.description || 'Failed to add to cart');
      }

      const data = await response.json();
      cardDebug('[BeezFy Card v3] Added to cart successfully:', data);

      // Use simple notification for fallback
      this.showNotification('Prodotto aggiunto al carrello', 'success');

      await this.refreshCartSections(['cart-drawer', 'cart-icon-bubble']);
      await this.updateCartCount();

      this.trackEvent('add_to_cart', {
        variant_id: variantId,
        quantity: quantity
      });

    } catch (error) {
      this.showNotification(error.message || 'Errore nell\'aggiunta al carrello', 'error');
      console.error('[BeezFy Card v3] Add to cart error:', error);
    } finally {
      this.setButtonLoading(button, false);
    }
  }
  
  async refreshCartSections(sections = ['cart-drawer', 'cart-icon-bubble']) {
    try {
      const url = `/?sections=${sections.join(',')}`;
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      
      if (!response.ok) return;
      
      const html = await response.json();
      sections.forEach((id) => {
        const target = document.getElementById(id);
        if (target && html[id]) {
          target.innerHTML = html[id];
        }
      });
      
      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      cardDebug('[BeezFy Card v3] Could not refresh sections:', error);
    }
  }
  
  async openVariantWizard(productHandle, productId) {
    cardDebug('[BeezFy Card v3] Opening variant wizard for:', { productHandle, productId });

    // Validate inputs
    if (!productHandle || productHandle === 'undefined' || productHandle === 'null') {
      console.error('[BeezFy Card v3] Invalid product handle:', productHandle);
      this.showNotification('Errore: prodotto non valido', 'error');
      return false;
    }

    try {
      const response = await fetch(`/products/${productHandle}.js`);

      if (!response.ok) {
        console.error('[BeezFy Card v3] Failed to fetch product:', response.status);
        // Non fare redirect, mostra errore
        this.showNotification('Prodotto non disponibile', 'error');
        return false;
      }

      const product = await response.json();
      cardDebug('[BeezFy Card v3] Product fetched:', product);

      if (!product || !Array.isArray(product.variants) || product.variants.length === 0) {
        console.error('[BeezFy Card v3] Invalid product data:', product);
        // Non fare redirect, mostra errore
        this.showNotification('Dati prodotto non validi', 'error');
        return false;
      }

      // Respect global wizard settings
      const theme = window.themeSettings || {};
      if (theme.enable_variant_wizard === false || theme.wizard_trigger_mode === 'manual') {
        cardDebug('[BeezFy Card v3] Wizard disabled by settings');
        return false;
      }

      const optionNames = Array.isArray(product.options)
        ? product.options.map(opt => (typeof opt === 'string' ? opt : opt.name)).filter(n => n && n.toLowerCase() !== 'title')
        : [];

      cardDebug('[BeezFy Card v3] Product options:', optionNames);

      // Check if product actually has meaningful variants
      const hasRealVariants = product.variants.length > 1 &&
                              optionNames.length > 0 &&
                              !product.variants.every(v => v.title === 'Default Title');

      // No options to choose or single variant – add directly
      if (!hasRealVariants) {
        cardDebug('[BeezFy Card v3] No real variants, adding directly');
        const variant = product.variants.find(v => v.id == productId) || product.variants[0];
        if (variant && variant.available) {
          await this.addToCart(variant.id, 1);
        } else {
          this.showNotification('Prodotto esaurito', 'error');
        }
        return true;
      }

      // Use unified variant wizard system
      if (window.BeezfyVariantWizard || window.variantWizard) {
        const wizardSystem = window.BeezfyVariantWizard || window.variantWizard;

        cardDebug('[BeezFy Card v3] Opening unified variant wizard with product:', product);

        // Open wizard with full product data
        await wizardSystem.open(product);

        this.trackEvent('variant_wizard_open', {
          product_id: productId,
          product_handle: productHandle
        });
        return true;
      }

      // Fallback: Use custom element if available
      if (customElements.get('variant-wizard')) {
        const wizardData = {
          id: product.id,
          title: product.title || 'Seleziona variante',
          options: optionNames,
          variants: product.variants,
          selectedVariant: product.variants.find(v => v.id == productId) || null
        };

        cardDebug('[BeezFy Card v3] Creating wizard with data:', wizardData);

        const wizard = document.createElement('variant-wizard');
        wizard.dataset.productJson = JSON.stringify(wizardData);
        document.body.appendChild(wizard);

        this.trackEvent('variant_wizard_open', {
          product_id: productId,
          product_handle: productHandle
        });
        return true;
      }

      // Final fallback
      cardDebug('[BeezFy Card v3] No wizard system available');
      this.showNotification('Seleziona le opzioni nella pagina prodotto', 'info');
      return false;
    } catch (error) {
      console.error('[BeezFy Card v3] Error opening variant wizard:', error);
      this.showNotification('Errore nel caricamento del prodotto', 'error');
      return false;
    }
  }
  
  // Modal functionality removed - products with variants go to product page
  
  async openQuickView(productHandle, triggerEl) {
    this._qvTrigger = triggerEl || null;
    // Salva l'URL/titolo di partenza (la collezione/loop dove sei): lo scrubber del quickview
    // cambia l'URL con replaceState mentre sfogli; al close lo ripristiniamo così reload torna qui.
    try { this._qvReturnUrl = window.location.href; this._qvReturnTitle = document.title; } catch (e) {}
    // Lo scrubber deve essere SEMPRE aperto a ogni nuova apertura del quickview
    // (lo stato "chiuso" resta valido solo durante la sessione di quel quickview, non tra aperture).
    try { sessionStorage.removeItem('bpcQvScrubClosed'); } catch (e) {}
    cardDebug('[BeezFy Card v3] Opening quick view for:', productHandle);

    try {
      // Collezione di provenienza: ricavala dal link della card (costruito con `within: collection`)
      // o, in mancanza, dall'URL corrente. Escludi all/frontpage → in quei casi la QV cade sul
      // fallback (prima collezione reale del prodotto), coerente col PDP.
      let collPrefix = '';
      try {
        const scope = (triggerEl && triggerEl.querySelector) ? triggerEl : document;
        const link = scope.querySelector('a[href*="/collections/"]');
        let m = ((link && link.getAttribute('href')) || '').match(/\/collections\/([^\/?#]+)/);
        if (!m) m = (window.location.pathname || '').match(/\/collections\/([^\/?#]+)/);
        const coll = m ? m[1] : '';
        if (coll && coll !== 'all' && coll !== 'frontpage') collPrefix = `/collections/${coll}`;
      } catch (e) {}
      // Fetch product data (con contesto collezione quando disponibile)
      const response = await fetch(`${collPrefix}/products/${productHandle}?view=quickview`);
      
      if (!response.ok) {
        // Fallback to product page if quickview template doesn't exist
        window.location.href = `/products/${productHandle}`;
        return;
      }
      
      const html = await response.text();
      
      // Create modal if it doesn't exist
      let modal = document.getElementById('beezfy-quickview-modal');
      if (!modal) {
        modal = document.createElement('div');
        modal.id = 'beezfy-quickview-modal';
        modal.className = 'beezfy-quickview-modal';
        modal.innerHTML = `
          <div class="beezfy-quickview-modal__overlay"></div>
          <div class="beezfy-quickview-modal__content">
            <button class="beezfy-quickview-modal__close" aria-label="Chiudi">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <div class="beezfy-quickview-modal__body"></div>
          </div>
        `;
        document.body.appendChild(modal);
        
        // Add close handlers
        modal.querySelector('.beezfy-quickview-modal__overlay').addEventListener('click', () => {
          this.closeQuickView();
        });
        modal.querySelector('.beezfy-quickview-modal__close').addEventListener('click', () => {
          this.closeQuickView();
        });
        // pulsante chiudi del template "feed" + ESC
        modal.addEventListener('click', (e) => {
          if (e.target.closest && e.target.closest('[data-bpc-quickview-close]')) this.closeQuickView();
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && modal.classList.contains('active')) this.closeQuickView();
        });
      }
      
      // Update content
      modal.querySelector('.beezfy-quickview-modal__body').innerHTML = html;
      // Quick view "feed style" fullscreen
      modal.classList.toggle('beezfy-quickview-modal--feed', !!modal.querySelector('.bpc-qv'));

      // Show modal
      modal.classList.add('active');
      document.body.classList.add('overflow-hidden');

      // Animazione premium FLIP: espande dalla card alla posizione finale (fullscreen mobile / frame desktop)
      try {
        const content = modal.querySelector('.beezfy-quickview-modal__content');
        const trig = this._qvTrigger;
        if (content && trig && trig.getBoundingClientRect) {
          const card = trig.getBoundingClientRect();
          if (card.width > 0 && card.height > 0) {
            content.style.transition = 'none';
            content.style.transform = 'none';
            content.style.opacity = '1';
            void content.offsetWidth; // layout finale
            const cr = content.getBoundingClientRect();
            if (cr.width > 0 && cr.height > 0) {
              this._qvFromRect = card;
              const dx = card.left - cr.left, dy = card.top - cr.top;
              const sx = Math.max(0.04, card.width / cr.width), sy = Math.max(0.04, card.height / cr.height);
              content.style.transformOrigin = 'top left';
              content.style.willChange = 'transform, opacity';
              content.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(' + sx + ',' + sy + ')';
              content.style.opacity = '0.55';
              void content.offsetWidth;
              requestAnimationFrame(() => {
                content.style.transition = 'transform .5s cubic-bezier(.16,1,.3,1), opacity .34s ease';
                content.style.transform = 'none';
                content.style.opacity = '1';
              });
            }
          }
        }
      } catch (e) {}

      // Reinitialize any scripts in the quickview content
      this.initQuickViewScripts(modal);

      // Card di variazione esplosa: apri la QV già sul colore corrispondente.
      // Il server non onora ?variant= sulla vista quickview, quindi selezioniamo lo
      // swatch lato client (solo un click DOM: nessuna richiesta extra).
      try {
        const trig = triggerEl;
        const cEl = (trig && trig.matches && trig.matches('[data-card-variant-color]')) ? trig
                  : (trig && trig.closest ? trig.closest('[data-card-variant-color]') : null);
        const wantColor = ((cEl && cEl.getAttribute('data-card-variant-color')) || '').trim().toLowerCase();
        if (wantColor) {
          setTimeout(() => {
            try {
              let target = null;
              modal.querySelectorAll('.bpc-swatch[data-bpc-qv-color]').forEach((s) => {
                if (!target && (s.getAttribute('data-bpc-qv-color') || '').trim().toLowerCase() === wantColor) target = s;
              });
              if (target && !target.classList.contains('bpc-swatch--active')) target.click();
            } catch (e) {}
          }, 60);
        }
      } catch (e) {}

    } catch (error) {
      console.error('[BeezFy Card v3] Error opening quick view:', error);
      // Fallback to product page
      window.location.href = `/products/${productHandle}`;
    }
    
    this.trackEvent('quick_view_open', {
      product_handle: productHandle
    });
  }
  
  closeQuickView() {
    const modal = document.getElementById('beezfy-quickview-modal');
    if (!modal) return;
    // Ripristina l'URL/titolo di partenza (annulla il replaceState fatto dallo scrubber):
    // così chiudendo e ricaricando torni alla collezione/loop, non all'ultimo prodotto sfogliato.
    try {
      if (this._qvReturnUrl) {
        window.history.replaceState({}, '', this._qvReturnUrl);
        if (this._qvReturnTitle) document.title = this._qvReturnTitle;
      }
    } catch (e) {}
    const content = modal.querySelector('.beezfy-quickview-modal__content');
    const card = this._qvFromRect;
    if (content && card && modal.classList.contains('beezfy-quickview-modal--feed')) {
      const cr = content.getBoundingClientRect();
      const dx = card.left - cr.left, dy = card.top - cr.top;
      const sx = Math.max(0.04, card.width / cr.width), sy = Math.max(0.04, card.height / cr.height);
      content.style.transformOrigin = 'top left';
      content.style.transition = 'transform .42s cubic-bezier(.4,0,.2,1), opacity .3s ease';
      content.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(' + sx + ',' + sy + ')';
      content.style.opacity = '0';
      const done = () => {
        modal.classList.remove('active', 'beezfy-quickview-modal--feed');
        document.body.classList.remove('overflow-hidden');
        content.style.transition = 'none'; content.style.transform = ''; content.style.opacity = ''; content.style.willChange = '';
        content.removeEventListener('transitionend', done);
      };
      content.addEventListener('transitionend', done);
      setTimeout(done, 520);
    } else {
      modal.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
    }
  }
  
  initQuickViewScripts(modal) {
    // Re-esegui gli <script> inline della quick view (gallery / swatch / ATC) — innerHTML non li esegue
    try {
      modal.querySelectorAll('.beezfy-quickview-modal__body script').forEach((old) => {
        if (old.src) return;
        const s = document.createElement('script');
        if (old.type) s.type = old.type;
        s.textContent = old.textContent;
        old.replaceWith(s);
      });
    } catch (e) {}

    // Initialize variant selectors if present
    const variantSelectors = modal.querySelectorAll('[data-variant-selector]');
    if (variantSelectors.length > 0) {
      // Re-init variant functionality
      cardDebug('[BeezFy Card v3] Initializing quickview variant selectors');
    }
    
    // Initialize add to cart forms
    const forms = modal.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const formData = new FormData(form);
        const variantId = formData.get('id');
        const quantity = formData.get('quantity') || 1;
        await this.addToCart(variantId, quantity);
        this.closeQuickView();
      });
    });
  }
  
  toggleWishlist(productId, button) {
    // Sync with existing wishlist system
    const wishlistData = JSON.parse(localStorage.getItem('beezfy-wishlist')) || [];
    const isActive = wishlistData.includes(productId);
    
    if (isActive) {
      // Remove from wishlist
      const newWishlist = wishlistData.filter(id => id !== productId);
      localStorage.setItem('beezfy-wishlist', JSON.stringify(newWishlist));
      button.classList.remove('bz-card__action-btn--active');
      this.updateWishlistIcon(button, false);
      this.showNotification('Rimosso dalla wishlist', 'info');
    } else {
      // Add to wishlist
      wishlistData.push(productId);
      localStorage.setItem('beezfy-wishlist', JSON.stringify(wishlistData));
      button.classList.add('bz-card__action-btn--active');
      this.updateWishlistIcon(button, true);
      this.showNotification('Aggiunto alla wishlist', 'success');
    }
    
    // Update local copy
    this.wishlist = JSON.parse(localStorage.getItem('beezfy-wishlist')) || [];
    
    // Update header count
    const countElement = document.getElementById('wishlist-count-bubble');
    if (countElement) {
      countElement.textContent = this.wishlist.length;
      countElement.hidden = this.wishlist.length === 0;
    }
    
    this.trackEvent('wishlist_toggle', {
      product_id: productId,
      action: isActive ? 'remove' : 'add'
    });
  }
  
  updateWishlistIcon(button, isActive) {
    const emptyIcon = button.querySelector('.bz-icon--heart-empty');
    const filledIcon = button.querySelector('.bz-icon--heart-filled');
    
    if (emptyIcon && filledIcon) {
      emptyIcon.style.display = isActive ? 'none' : 'block';
      filledIcon.style.display = isActive ? 'block' : 'none';
    }
  }
  
  toggleCompare(productId, button) {
    // Sync with session storage
    const compareData = JSON.parse(sessionStorage.getItem('beezfy-compare')) || [];
    const isActive = compareData.includes(productId);
    
    if (isActive) {
      // Remove from compare
      const newCompare = compareData.filter(id => id !== productId);
      sessionStorage.setItem('beezfy-compare', JSON.stringify(newCompare));
      button.classList.remove('bz-card__action-btn--active');
      this.showNotification('Rimosso dal confronto', 'info');
    } else {
      // Check max limit
      if (compareData.length >= 4) {
        this.showNotification('Massimo 4 prodotti per il confronto', 'warning');
        return;
      }
      
      // Add to compare
      compareData.push(productId);
      sessionStorage.setItem('beezfy-compare', JSON.stringify(compareData));
      button.classList.add('bz-card__action-btn--active');
      this.showNotification('Aggiunto al confronto', 'success');
    }
    
    // Update local copy
    this.compare = JSON.parse(sessionStorage.getItem('beezfy-compare')) || [];
    
    // Update compare count if element exists
    const countElement = document.getElementById('compare-count-bubble');
    if (countElement) {
      countElement.textContent = this.compare.length;
      countElement.hidden = this.compare.length === 0;
    }
    
    this.trackEvent('compare_toggle', {
      product_id: productId,
      action: isActive ? 'remove' : 'add'
    });
  }
  
  async shareProduct(url, title) {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          url: url
        });
        this.trackEvent('share', {
          method: 'native',
          url: url
        });
      } catch (err) {
        // User cancelled share
      }
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(url).then(() => {
        this.showNotification('Link copied to clipboard', 'success');
        this.trackEvent('share', {
          method: 'clipboard',
          url: url
        });
      });
    }
  }

  cacheInitialMedia(card) {
    const primaryImage = card.querySelector('.bz-card__media-image--primary');
    if (!primaryImage) return;

    const currentSrc = primaryImage.currentSrc || primaryImage.src;
    const currentSrcset = primaryImage.getAttribute('srcset');

    if (!primaryImage.dataset.initialSrc && currentSrc) {
      primaryImage.dataset.initialSrc = currentSrc;
    }
    if (!primaryImage.dataset.initialSrcset && currentSrcset) {
      primaryImage.dataset.initialSrcset = currentSrcset;
    }

    if (!primaryImage.dataset.originalSrc && currentSrc) {
      primaryImage.dataset.originalSrc = currentSrc;
    }
    if (!primaryImage.dataset.originalSrcset && currentSrcset) {
      primaryImage.dataset.originalSrcset = currentSrcset;
    }

    if (!card.dataset.selectedVariantImage && currentSrc) {
      card.dataset.selectedVariantImage = currentSrc;
    }

    const selectedVariantIdInput = card.querySelector('[name="id"]');
    if (selectedVariantIdInput && !card.dataset.selectedVariantId) {
      card.dataset.selectedVariantId = selectedVariantIdInput.value || '';
    }
  }

  async updateProductImage(card, variantId, meta = {}) {
    if (!variantId && !meta?.swatchImage && !meta?.optionValue) return;

    const mediaContainer = card.querySelector('[data-media-container]');
    const hasGallery = mediaContainer?.hasAttribute('data-gallery-enabled');
    const primaryImage = card.querySelector('.bz-card__media-image--primary');
    const secondaryImage = card.querySelector('.bz-card__media-image--secondary');
    const productHandle = card.dataset.productHandle;

    // Handle gallery mode separately (in modalita' galleria non esiste
    // .bz-card__media-image--primary: va sempre gestito qui, anche se la classe
    // gallery JS non fosse ancora caricata — updateGalleryForVariant ha fallback DOM).
    if (hasGallery) {
      this.updateGalleryForVariant(card, variantId, meta);
      return;
    }

    if (!primaryImage || !productHandle) return;

    try {
      const media = await this.getVariantMedia(card, variantId, {
        optionName: meta.optionName,
        optionValue: meta.optionValue,
        productHandle,
        swatchImage: meta.swatchImage
      });

      if (media?.image) {
        // Store initial image only once for fallback/debug
        if (!primaryImage.dataset.initialSrc) {
          primaryImage.dataset.initialSrc = primaryImage.src;
          primaryImage.dataset.initialSrcset = primaryImage.srcset;
        }

        // Update primary image with variant image
        const variantImageUrl = media.image;
        const targetSrc = this.formatImageUrl(variantImageUrl, 600) || variantImageUrl;

        // Generate responsive srcset
        const widths = [200, 400, 600, 800, 1200];
        const srcset = widths
          .map((w) => {
            const sizedUrl = this.formatImageUrl(variantImageUrl, w);
            return sizedUrl ? `${sizedUrl} ${w}w` : '';
          })
          .filter(Boolean)
          .join(', ');

        // Update image with smooth transition
        primaryImage.style.opacity = '0.8';
        setTimeout(() => {
          primaryImage.src = targetSrc;
          if (srcset) {
            primaryImage.srcset = srcset;
            primaryImage.dataset.originalSrcset = srcset;
          } else {
            primaryImage.removeAttribute('srcset');
            delete primaryImage.dataset.originalSrcset;
          }
          primaryImage.style.opacity = '1';
        }, 100);

        // Persist new base image for future previews/restores
        primaryImage.dataset.originalSrc = targetSrc;
        primaryImage.dataset.selectedVariantId = variantId || '';
        primaryImage.dataset.selectedVariantSrc = targetSrc;

        card.dataset.selectedVariantId = variantId || '';
        card.dataset.selectedVariantImage = targetSrc;

        // Restore secondary image visibility for hover swap
        if (secondaryImage) {
          secondaryImage.style.display = '';
          secondaryImage.style.opacity = '';
        }

        cardDebug('[BeezFy Card v3] Updated product image for variant:', variantId);
      }
    } catch (error) {
      cardDebug('[BeezFy Card v3] Error updating product image:', error);
    }
  }

  // Normalizza un URL immagine Shopify per il confronto: toglie query string e
  // suffisso taglia (_600x, _600x800, _600x600_crop_center...) lasciando il nome
  // file "base". Serve perche' la swatch usa width 800 e le slide width 600:
  // stessa foto, URL diversi.
  normalizeImageKey(url) {
    if (!url) return '';
    try {
      let s = String(url).split('?')[0];
      s = s.split('/').pop() || s;
      s = s.replace(/_(\d+)x(\d+)?(_[a-z_]+)*(?=\.(jpg|jpeg|png|gif|webp)$)/i, '');
      return s.toLowerCase();
    } catch (e) {
      return String(url).toLowerCase();
    }
  }

  async updateGalleryForVariant(card, variantId, meta = {}) {
    const mediaContainer = card.querySelector('[data-media-container]');
    const gallery = mediaContainer?.querySelector('[data-card-gallery]');
    const productHandle = card.dataset.productHandle;

    if (!gallery || !productHandle) return;

    try {
      // Get variant media
      const media = await this.getVariantMedia(card, variantId, {
        optionName: meta.optionName,
        optionValue: meta.optionValue,
        productHandle,
        swatchImage: meta.swatchImage
      });

      if (!media?.image) return;

      const variantImageUrl = media.image;
      const variantKey = this.normalizeImageKey(variantImageUrl);
      const slides = gallery.querySelectorAll('[data-gallery-slide]');
      const galleryInstance = mediaContainer._bzfyGallery;

      // Cerca la slide che contiene la foto della variante. IMPORTANTE: le slide
      // pigre hanno solo data-src (nessun src), quindi va letto anche quello,
      // altrimenti il match fallisce sempre e la foto non cambia mai.
      let targetSlideIndex = -1;
      slides.forEach((slide, index) => {
        if (targetSlideIndex !== -1) return;
        const img = slide.querySelector('img');
        if (!img) return;
        const slideUrl = img.currentSrc || img.getAttribute('src') || img.dataset.src || '';
        if (slideUrl && this.normalizeImageKey(slideUrl) === variantKey) {
          targetSlideIndex = index;
        }
      });

      cardDebug('[BeezFy Card v3] Variant image search:', {
        variantImageUrl,
        variantKey,
        targetSlideIndex,
        totalSlides: slides.length
      });

      if (targetSlideIndex !== -1) {
        // La foto della variante e' fra le slide: naviga fino a quella.
        if (galleryInstance && typeof galleryInstance.goToSlide === 'function') {
          galleryInstance.goToSlide(targetSlideIndex);
        } else {
          // Fallback difensivo (istanza gallery non pronta): attiva la slide e
          // promuovi la SUA sola immagine pigra (non tocca le altre → nessun
          // caricamento di massa, resta safe per il crash del load-more su iPhone).
          const target = slides[targetSlideIndex];
          target?.querySelectorAll('img[data-lazy-slide][data-src]').forEach((im) => {
            if (im.dataset.srcset) im.setAttribute('srcset', im.dataset.srcset);
            im.setAttribute('src', im.dataset.src);
            im.removeAttribute('data-src'); im.removeAttribute('data-srcset'); im.removeAttribute('data-lazy-slide');
          });
          slides.forEach((s, i) => s.classList.toggle('bz-card__gallery-slide--active', i === targetSlideIndex));
        }
      } else {
        // La variante NON e' fra le slide (foto oltre il limite, o non in galleria):
        // scambia direttamente l'immagine della slide attiva cosi' la foto cambia comunque.
        const activeSlide = gallery.querySelector('.bz-card__gallery-slide--active') || slides[0];
        const img = activeSlide?.querySelector('img');
        if (img) {
          const targetSrc = this.formatImageUrl(variantImageUrl, 600) || variantImageUrl;
          const srcset = [200, 400, 600, 800, 1200]
            .map((w) => { const u = this.formatImageUrl(variantImageUrl, w); return u ? `${u} ${w}w` : ''; })
            .filter(Boolean)
            .join(', ');
          img.style.opacity = '0.8';
          setTimeout(() => {
            img.src = targetSrc;
            if (srcset) img.srcset = srcset; else img.removeAttribute('srcset');
            img.style.opacity = '1';
          }, 100);
        }
      }

      // Update card metadata
      card.dataset.selectedVariantId = variantId || '';
      card.dataset.selectedVariantImage = variantImageUrl;

      cardDebug('[BeezFy Card v3] Updated gallery for variant:', variantId, 'to slide:', targetSlideIndex);
    } catch (error) {
      cardDebug('[BeezFy Card v3] Error updating gallery for variant:', error);
    }
  }

  async previewVariant(card, variantId, meta = {}) {
    if (!variantId && !meta?.swatchImage && !meta?.optionValue) return;

    const primaryImage = card.querySelector('.bz-card__media-image--primary');
    const productHandle = card.dataset.productHandle;

    if (!primaryImage || !productHandle) return;

    try {
      // Store original state for restoration
      if (!primaryImage.dataset.originalSrc) {
        primaryImage.dataset.originalSrc = primaryImage.src;
        primaryImage.dataset.originalSrcset = primaryImage.srcset;
      }

      const media = await this.getVariantMedia(card, variantId, {
        optionName: meta.optionName,
        optionValue: meta.optionValue,
        productHandle,
        swatchImage: meta.swatchImage
      });

      if (media?.image) {
        const variantImageUrl = media.image;
        const targetSrc = this.formatImageUrl(variantImageUrl, 600) || variantImageUrl;

        const widths = [200, 400, 600, 800, 1200];
        const srcset = widths
          .map((w) => {
            const sizedUrl = this.formatImageUrl(variantImageUrl, w);
            return sizedUrl ? `${sizedUrl} ${w}w` : '';
          })
          .filter(Boolean)
          .join(', ');

        // Quick preview with fade effect
        primaryImage.style.transition = 'opacity 0.2s ease';
        primaryImage.style.opacity = '0.7';

        setTimeout(() => {
          primaryImage.src = targetSrc;
          if (srcset) {
            primaryImage.srcset = srcset;
          } else if (primaryImage.dataset.originalSrcset) {
            primaryImage.srcset = primaryImage.dataset.originalSrcset;
          } else {
            primaryImage.removeAttribute('srcset');
          }
          primaryImage.style.opacity = '1';
        }, 50);
      }
    } catch (error) {
      cardDebug('[BeezFy Card v3] Error in preview variant:', error);
    }
  }

  async getVariantMedia(card, variantId, meta = {}) {
    const productHandle = meta.productHandle || card.dataset.productHandle;
    if (!productHandle && !variantId) return null;

    const cardCache = card.__beezfyMediaCache || (card.__beezfyMediaCache = new Map());
    const cacheKey = variantId || `${meta.optionName || 'option'}|${meta.optionValue || ''}`;

    if (cardCache.has(cacheKey)) {
      return cardCache.get(cacheKey);
    }

    let media = null;

    if (meta.swatchImage) {
      media = {
        image: meta.swatchImage,
        alt: meta.optionValue || card.dataset.productHandle,
        variantId: variantId || null,
        source: 'swatch'
      };
    }

    if (!media && this.swatchDataStore) {
      media = await this.swatchDataStore.resolveVariantMedia({
        productHandle,
        variantId,
        optionName: meta.optionName,
        optionValue: meta.optionValue
      });
    }

    cardCache.set(cacheKey, media);
    return media;
  }
  
  restoreOriginalImage(card) {
    const primaryImage = card.querySelector('.bz-card__media-image--primary');
    const secondaryImage = card.querySelector('.bz-card__media-image--secondary');

    if (primaryImage) {
      const fallbackSrc = primaryImage.dataset.originalSrc || primaryImage.dataset.selectedVariantSrc || primaryImage.dataset.initialSrc;
      const fallbackSrcset = primaryImage.dataset.originalSrcset || primaryImage.dataset.initialSrcset;

      if (!fallbackSrc) {
        if (secondaryImage) {
          secondaryImage.style.display = '';
          secondaryImage.style.opacity = '';
        }
        return;
      }

      // Restore with fade effect
      primaryImage.style.transition = 'opacity 0.2s ease';
      primaryImage.style.opacity = '0.7';

      setTimeout(() => {
        primaryImage.src = fallbackSrc;
        if (fallbackSrcset) {
          primaryImage.srcset = fallbackSrcset;
        } else {
          primaryImage.removeAttribute('srcset');
        }
        primaryImage.style.opacity = '1';
      }, 50);
    }

    // Restore secondary image visibility
    if (secondaryImage) {
      secondaryImage.style.display = '';
      secondaryImage.style.opacity = '';
    }
  }

  formatImageUrl(url, size = 600) {
    if (!url) return '';

    if (window.Shopify?.Image?.getSizedImageUrl) {
      try {
        return Shopify.Image.getSizedImageUrl(url, `${size}x`);
      } catch (error) {
        // Fallback to manual suffixing
      }
    }

    const match = url.match(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
    if (!match) {
      return url;
    }

    return url.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, `_${size}x.$1$2`);
  }

  updateSelectedVariant(card, variantId) {
    // Update hidden input if exists
    const variantInput = card.querySelector('[name="id"]');
    if (variantInput) {
      variantInput.value = variantId;
    }
    
    // Enable add to cart button
    const atcButton = card.querySelector('[data-atc-button]');
    if (atcButton) {
      atcButton.disabled = false;
    }
  }
  
  setButtonLoading(button, loading) {
    if (!button) return;
    
    const textSpan = button.querySelector('.bz-card__atc-btn-text');
    const spinnerSpan = button.querySelector('.bz-card__atc-btn-spinner');
    
    if (loading) {
      button.disabled = true;
      button.classList.add('bz-card__atc-btn--loading');
      if (textSpan) textSpan.style.visibility = 'hidden';
      if (spinnerSpan) spinnerSpan.style.display = 'block';
    } else {
      button.disabled = false;
      button.classList.remove('bz-card__atc-btn--loading');
      if (textSpan) textSpan.style.visibility = 'visible';
      if (spinnerSpan) spinnerSpan.style.display = 'none';
    }
  }
  
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update all cart count elements
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }
  
  openCartDrawer() {
    // Use unified drawer system
    if (window.beezfyDrawer && typeof window.beezfyDrawer.open === 'function') {
      window.beezfyDrawer.open('cart');
      return;
    }

    // Emit custom event for unified drawer system
    document.dispatchEvent(new CustomEvent('cart:item-added'));

    // Fallback: cart-drawer custom element
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }
      cartDrawer.classList.add('active');
      document.body.classList.add('overflow-hidden');

      // Also try setting drawer-open attribute
      cartDrawer.setAttribute('open', '');
      return;
    }

    // Method 2: cart-notification popup
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification) {
      if (typeof cartNotification.open === 'function') {
        cartNotification.open();
      } else {
        cartNotification.classList.add('animate', 'active');
      }
      return;
    }

    // Method 3: trigger cart icon bubble click
    const cartIconBubble = document.querySelector('#cart-icon-bubble, cart-icon-bubble');
    if (cartIconBubble) {
      const link = cartIconBubble.querySelector('a');
      if (link) {
        link.click();
        return;
      }
    }

    // Don't click cart icons that would cause navigation
    // Only open if it's a drawer trigger, not a direct link
    const cartIcon = document.querySelector('.header__icon--cart:not([href="/cart"])');
    if (cartIcon && cartIcon.getAttribute('data-drawer-trigger')) {
      cartIcon.click();
    }
    // If no drawer system found, just show a notification instead
    else {
      this.showNotification('Prodotto aggiunto al carrello', 'success');
    }
  }
  
  showATCPopup(data) {
    // Check if BeezFy ATC popup exists
    const atcPopup = document.querySelector('.bz-atc-popup, .beezfy-atc-popup');
    if (atcPopup) {
      atcPopup.classList.add('active');

      // Update popup content if needed
      const productTitle = atcPopup.querySelector('.bz-atc-popup__product-title');
      if (productTitle && data.product_title) {
        productTitle.textContent = data.product_title;
      }

      // Auto-close after 3 seconds
      setTimeout(() => {
        atcPopup.classList.remove('active');
      }, 3000);
    } else {
      // Fallback to notification if popup not found
      this.showNotification('Prodotto aggiunto al carrello', 'success');
    }
  }

  showNotification(message, type = 'info') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `bz-notification bz-notification--${type}`;
    notification.textContent = message;

    // Add to body
    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.classList.add('bz-notification--visible');
    }, 10);

    // Remove after delay
    setTimeout(() => {
      notification.classList.remove('bz-notification--visible');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }
  
  // Wishlist persistence
  loadWishlist() {
    const saved = localStorage.getItem('beezfy-wishlist');
    return saved ? JSON.parse(saved) : [];
  }
  
  saveWishlist() {
    localStorage.setItem('beezfy-wishlist', JSON.stringify(this.wishlist));
  }
  
  updateWishlistCount() {
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = this.wishlist.length;
    });
  }
  
  // Compare persistence
  loadCompare() {
    const saved = sessionStorage.getItem('beezfy-compare');
    return saved ? JSON.parse(saved) : [];
  }
  
  saveCompare() {
    sessionStorage.setItem('beezfy-compare', JSON.stringify(this.compare));
  }
  
  updateCompareCount() {
    document.querySelectorAll('[data-compare-count]').forEach(el => {
      el.textContent = this.compare.length;
    });
  }
  
  // Analytics
  initAnalytics() {
    if (!window.IntersectionObserver) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.dataset.tracked) {
          const card = this.cards.find(c => c.element === entry.target);
          if (card) {
            this.trackImpression(card);
            entry.target.dataset.tracked = 'true';
          }
        }
      });
    }, {
      threshold: 0.5
    });
    
    this.cards.forEach(card => {
      if (card.element.dataset.analyticsEnabled) {
        observer.observe(card.element);
      }
    });
  }
  
  trackImpression(card) {
    this.trackEvent('product_impression', {
      product_id: card.productId,
      product_handle: card.productHandle,
      index: card.index,
      layout: card.layout,
      context: card.context
    });
  }
  
  trackEvent(eventName, data) {
    // Google Analytics 4
    if (window.gtag) {
      window.gtag('event', eventName, data);
    }
    
    // Custom analytics
    document.dispatchEvent(new CustomEvent('beezfy:analytics', {
      detail: {
        event: eventName,
        data: data,
        timestamp: Date.now()
      }
    }));
    
    // Console log in development
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      cardDebug('BeezFy Analytics:', eventName, data);
    }
  }
  
  // Global event listeners
  initGlobalListeners() {
    // Listen for variant selections from wizard
    document.addEventListener('variant-selected', async (e) => {
      const { variantId } = e.detail || {};
      if (!variantId) return;

      cardDebug('[BeezFy Card v3] Variant selected from wizard:', { variantId });

      await this.addToCart(variantId, 1);
    });
    
    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      this.updateCartCount();
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.beezfyProductCardV3 = new BeezfyProductCardV3();
  });
} else {
  window.beezfyProductCardV3 = new BeezfyProductCardV3();
}

// Notification styles
const notificationStyles = `
  .bz-notification {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%) translateY(100px);
    padding: 14px 24px;
    border-radius: 8px;
    color: white;
    font-size: 14px;
    font-weight: 500;
    z-index: 10000;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  }
  
  .bz-notification--visible {
    transform: translateX(-50%) translateY(0);
  }
  
  .bz-notification--success {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  }
  
  .bz-notification--error {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  }
  
  .bz-notification--warning {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  }
  
  .bz-notification--info {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  }
`;

// Inject notification styles
if (!document.querySelector('#beezfy-notification-styles')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'beezfy-notification-styles';
  styleEl.textContent = notificationStyles;
  document.head.appendChild(styleEl);
}

})();
